import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import Icon from '../../../../../components/icons'
import Button from '../../../../../elements/button'
import { useParams } from 'react-router-dom'
import Text from '../../../../../elements/Typo/Text'
import { useExperimentApi } from '../../../../../store/api/hooks/experimentApiHook'
import toast from 'react-hot-toast'
import { useExperimentSelector } from '../../../../../store/selectors/experimentSelector'
import moment from 'moment'
import Groups from '../group'
import { formatNumberWithCommas, getSelectedOption, trackEvent } from '../../../../../_common/functions'
import Dropdown from '../../../../../elements/Form/dropdown'
import { showTrendAs } from '../../../../../_config'
import { useFieldArray, useForm } from 'react-hook-form'
import { StylesConfig } from 'react-select'
import { useFpLoader } from '../../../../../store/api/hooks/fpLoaderHook'

const getBackgroundColor = (isDisabled: boolean, isFocused: boolean, isSelected: boolean) => {
    if (isDisabled) {
        return '#FCFCFC'
    }
    if (isFocused) {
        return '#f5e00361'
    }
    if (isSelected) {
        return '#f5e003'
    }
    return '#ffffff'
}
const customStyleOption: StylesConfig = {
    control: (styles) => ({
        ...styles,
        border: '1px solid #d5dad4',
        minHeight: '1.2rem',
        boxShadow: 'none',
        height: '1.2rem',
        padding: '0rem',
        ':hover': {
            border: '1px solid #d5dad4',
            boxShadow: 'none',
        },
        ':focus': {
            border: '1px solid #d5dad4',
            outline: 'none',
            boxShadow: 'none',
        },
    }),

    option: (styles, { data, isDisabled, isFocused, isSelected }) => {
        return {
            ...styles,
            backgroundColor: getBackgroundColor(isDisabled, isFocused, isSelected),
            color: '#000000',
            cursor: isDisabled ? 'not-allowed' : 'default',
            border: '1px solid #f4f4f4',
            fontSize: '0.583rem',
            padding: '0rem',
            paddingLeft: '0.5rem',
        }
    },
}

interface ExperimentReviewModalProps {
    _ref: any
    success?: () => void
    viewOnly?: boolean
    measurementModalRef?: any
}

const ExperimentReviewModal: React.FC<ExperimentReviewModalProps> = ({ _ref, success = () => {}, viewOnly = false, measurementModalRef }) => {
    const { t } = useTranslation()
    const params: any = useParams()
    const experiment = useExperimentSelector()
    const experimentApi = useExperimentApi()

    const [showMetriceExpand, setShowMetriceExpand] = useState<boolean>(true)
    const { showLoader, hideLoader } = useFpLoader()
    const [showGroupExpand] = useState<boolean>(true)
    const { control } = useForm<any>()
    const {
        fields: kpis,
        append: kpisAppend,
        update: kpisUpdate,
        remove: kpisRemove,
    } = useFieldArray({
        control,
        name: 'kpis',
    })
    const updateExperiment = () => {
        const MeasurementStart: boolean = sessionStorage.getItem('M_START') == params.experiment
        const paramsData: any = {
            step: MeasurementStart ? 6 : 5,
            is_draft: !MeasurementStart,
            literal: params.experiment,
        }

        experimentApi.updateExperiment(
            paramsData,
            (data: any) => {
                if (!viewOnly) {
                    trackEvent('experiment_submitted', {
                        experiment: params.experiment,
                    })
                }
                success()
                _ref.current.close()
            },
            (error: any) => {
                trackEvent('wizard_submit_failed', { error_code: error?.message })
                toast.error(error?.message)
            },
        )
    }
    let grp_sum = 0
    experiment?.experiment_metadatas?.interventions[0]?.group_configs?.map((group: any, i: any) => {
        grp_sum += group.group_size
    })

    const submitKpiDirection = (i: any) => {
        showLoader()
        const payload: any = {}
        payload.kpis = kpis
            .filter((kpi: any) => {
                return kpi.is_custom
            })
            .map((kpi: any) => {
                return {
                    literral_value: kpi.literral_value,
                    kpi_direction: kpi.kpi_direction?.value,
                }
            })
        payload.literal = experiment.experiment_literal
        console.log(payload)
        experimentApi.updateKpiDirection(
            payload,
            (resp: any) => {
                kpisUpdate(i, { ...kpis[i], edit_mode: false })
                hideLoader()
            },
            (err: any) => {
                toast.error(err?.message)
                kpisUpdate(i, { ...kpis[i], edit_mode: false })
                hideLoader()
            },
        )
    }
    useEffect(() => {
        if (experiment?.experiment_metadatas?.metrices) {
            kpisRemove()
            experiment?.experiment_metadatas?.metrices.forEach((kpi: any) => {
                kpisAppend({ ...kpi, edit_mode: false, kpi_direction: getSelectedOption(kpi?.kpi_direction?.value || kpi.trends_as || 'positive_upword', showTrendAs) })
            })
        }
    }, [experiment])

    return (
        <dialog ref={_ref} className="modal">
            <div className="modal-box w-[77.667rem] max-w-5xl rounded-[0.833rem] !p-0">
                <div className="bg-[#325A6D1A;] h-[7.375rem] p-5">
                    <Text size="md" className="mb-2">
                        {t('Experiment Summary')}
                    </Text>
                    <div className="flex items-center justify-between">
                        <Text size="xl" data-testid="experiment-title" className="font-semibold">
                            {experiment?.experiment_name}
                        </Text>
                        <div className="bg-white rounded-[0.833rem] px-2 leading-5 text-[0.667rem] text-[#616161]">
                            {t('Expt. Type :')} <span className="font-semibold text-black">{experiment?.experiment_type?.title ?? '-'}</span>
                        </div>
                    </div>
                </div>
                <div className="p-5 pt-0">
                    <div className="bg-white rounded-[0.417rem] mb-4 -mt-10 shadow-1 border border-[#E5E5E5] p-3">
                        <Text size="md" className="font-semibold mb-3">
                            {t('Context')}
                        </Text>
                        <Text size="sm" className="font-semibold">
                            {t('Problem Statement :')}
                        </Text>
                        <Text size="sm" className="text-[#616161] mb-3">
                            {experiment?.problem_statement}
                        </Text>
                        <div className="grid grid-cols-3">
                            <div className="col-span-2 border-r border-[#D9D9D9] pr-3">
                                <Text size="sm" className="font-semibold">
                                    {t('Use case :')}
                                </Text>
                                <Text size="sm" className="text-[#616161] mb-2">
                                    {experiment?.use_case?.title}
                                </Text>
                                <Text size="sm" className="font-semibold">
                                    {t('Hypothesis :')}
                                </Text>
                                <Text size="sm" className="text-[#616161] break-words">
                                    {experiment?.hypothesis}
                                </Text>
                            </div>
                            <div className="pl-3">
                                <Text size="sm" className="font-semibold mb-2">
                                    {t('Information :')}
                                </Text>
                                <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-1 ">
                                        <Icon name="briefcase" size={0.833} className="text-[#616161]" />
                                        <Text size="sm" className="!text-[#616161]">
                                            {t('Business Function :')}
                                        </Text>
                                    </div>
                                    <Text size="sm" className="!text-black font-semibold">
                                        {experiment?.business_function?.title}
                                    </Text>
                                </div>
                                <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-1 ">
                                        <Icon name="earth " size={0.833} className="text-[#616161]" />
                                        <Text size="sm" className="!text-[#616161]">
                                            {t('Country :')}
                                        </Text>
                                    </div>
                                    <Text size="sm" className="!text-black font-semibold">
                                        {experiment?.country?.title}
                                    </Text>
                                </div>
                                <div className="flex items-center justify-between mb-1">
                                    <div className="flex items-center gap-1 ">
                                        <Icon name="calendar" size={0.833} className="text-[#616161]" />
                                        <Text size="sm" className="!text-[#616161]">
                                            {t('Date :')}
                                        </Text>
                                    </div>
                                    <Text size="sm" className="!text-black font-semibold">
                                        {moment(experiment?.start_date).format('MM-DD-YYYY')} ~ {moment(experiment?.end_date).format('MM-DD-YYYY')}
                                    </Text>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="flex items-center justify-between">
                            <Text size="md" className="font-semibold">
                                {t('Metrics & Success Criteria:')}
                            </Text>

                            <Icon name="down" onClick={() => setShowMetriceExpand(!showMetriceExpand)} className={`transition-all ${showMetriceExpand ? 'rotate-0' : '-rotate-90'}`}></Icon>
                        </div>

                        <div className={`mt-1 transition-[max-height]  ${showMetriceExpand ? 'max-h-[8rem]' : 'max-h-0 overflow-hidden'}`}>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <Text size="sm" className="font-semibold">
                                        {t('Metrics:')}
                                    </Text>
                                    <div className="border border-[#F3F1F1] rounded-[0.417rem]  h-[6.167rem] grid grid-rows-4">
                                        <div className={`bg-[#ECECEC] grid ${viewOnly ? 'grid-cols-3' : 'grid-cols-2'}`}>
                                            <div className="border-r border-[#BEC0C7] flex items-center justify-center">
                                                <Text size="sm" className="font-semibold text-[#35353A] leading-none">
                                                    {t('KPI of Interest')}
                                                </Text>
                                            </div>
                                            <div className="flex items-center justify-center">
                                                <Text size="sm" className="font-semibold text-[#35353A] leading-none">
                                                    {t('KPI Type')}
                                                </Text>
                                            </div>
                                            {viewOnly && (
                                                <div className="flex items-center justify-center">
                                                    <Text size="sm" className="font-semibold text-[#35353A] leading-none">
                                                        {t('Show Trend')}
                                                    </Text>
                                                </div>
                                            )}
                                        </div>

                                        {Array.isArray(kpis) &&
                                            [1, 2, 3].map((key: number, i: number) => {
                                                const metric: any = kpis[i] || {}

                                                return (
                                                    <div className={`grid ${viewOnly ? 'grid-cols-3' : 'grid-cols-2'} ${i === 1 ? 'bg-[#E5B61112]' : ''}`} key={`kpi-data-${key}`}>
                                                        <div className="flex items-center justify-center">
                                                            <Text size="sm" className="text-black leading-none relative">
                                                                {metric.literral_value ?? '-'}

                                                                {i == 0 && <span className="top-[50%] -right-2 translate-x-[100%] translate-y-[-50%] absolute px-1 py-1 leading-none h-auto badge badge-primary text-[0.5rem]">{t('Primary')}</span>}
                                                            </Text>
                                                        </div>
                                                        <div className="flex items-center justify-center">
                                                            <Text size="sm" className="text-black leading-none">
                                                                {metric.kpi_type ?? '-'}
                                                            </Text>
                                                        </div>
                                                        {viewOnly && (
                                                            <div className="flex items-center justify-center w-full">
                                                                {metric.is_custom === true ? (
                                                                    <div className="flex gap-2 items-center w-full">
                                                                        {metric.edit_mode ? (
                                                                            <>
                                                                                <Dropdown
                                                                                    className="w-full flex-grow"
                                                                                    required={true}
                                                                                    value={getSelectedOption(metric?.kpi_direction?.value, showTrendAs)}
                                                                                    onChange={(value: any) => {
                                                                                        kpisUpdate(i, { ...metric, kpi_direction: value })
                                                                                    }}
                                                                                    customStyleOption={customStyleOption}
                                                                                    options={showTrendAs}
                                                                                    data-testid={`kpi-dropdown-${i}`}
                                                                                />
                                                                                <Icon
                                                                                    onClick={() => {
                                                                                        submitKpiDirection(i)
                                                                                    }}
                                                                                    name="circle-check-tick"
                                                                                    className="text-green-700"
                                                                                    size={'0.90'}
                                                                                    data-testid={`kpi-submit-icon-${i}`}
                                                                                />
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <Text size="sm" className="text-black leading-none">
                                                                                    {t(metric.kpi_direction?.label)}
                                                                                </Text>
                                                                                <Icon
                                                                                    name="edit"
                                                                                    size={'0.60'}
                                                                                    onClick={() => {
                                                                                        kpisUpdate(i, { ...metric, edit_mode: true })
                                                                                    }}
                                                                                    data-testid={`edit-icon-${i}`}
                                                                                />
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                ) : (
                                                                    '--'
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                )
                                            })}
                                    </div>
                                </div>

                                <div>
                                    <Text size="sm" className="font-semibold">
                                        {t('Success Criteria:')}
                                    </Text>
                                    <div className="border border-[#F3F1F1] rounded-[0.417rem] overflow-hidden h-[6.167rem] grid grid-rows-3">
                                        <div className="flex items-center justify-between px-2">
                                            <Text size="sm" className="text-[#616161] leading-none">
                                                {t('Minimum Uplift for Success')}
                                            </Text>
                                            <Text size="sm" className="font-semibold text-[#35353A] leading-none">
                                                {experiment?.success_criteria?.[0]?.minimum_uplift ?? ''}%
                                            </Text>
                                        </div>
                                        <div className="flex items-center justify-between px-2">
                                            <Text size="sm" className="text-[#616161] leading-none">
                                                {t('Confidence Level (%)')}
                                            </Text>
                                            <Text size="sm" className="font-semibold text-[#35353A] leading-none">
                                                {(() => {
                                                    const cl = Number(experiment?.success_criteria?.[0]?.confidence_level ?? 0)
                                                    return Number.isInteger(cl) ? cl : cl.toFixed(2)
                                                })()}
                                                %
                                            </Text>
                                        </div>
                                        <div className="flex items-center justify-between px-2">
                                            <Text size="sm" className="text-[#616161] leading-none">
                                                {t('Sample Size')}
                                            </Text>
                                            <Text size="sm" className="font-semibold text-[#35353A] leading-none">
                                                {grp_sum ? formatNumberWithCommas(grp_sum) : '-'}
                                            </Text>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-4">
                        <div className="flex items-center gap-2 border border-[#E5E5E5] rounded-[0.417rem] p-2 max-w-full">
                            <Text size="sm" className="font-semibold flex-shrink-0 whitespace-nowrap">
                                {t('Data Used for Design :')}
                            </Text>
                            <Text
                                size="sm"
                                className="px-2 bg-primary rounded-[0.717rem] !leading-4 flex-shrink whitespace-nowrap truncate"
                                title={experiment?.data_mappings?.delta_design_table_name ?? experiment?.data_mappings?.user_created_table_name ?? '-'}
                            >
                                {experiment?.data_mappings?.delta_design_table_name ?? experiment?.data_mappings?.user_created_table_name ?? '-'}
                            </Text>
                        </div>
                        {moment(experiment?.start_date).isAfter(moment()) && !viewOnly && experiment?.step == 4 ? (
                            <div></div>
                        ) : (
                            <div className="flex items-center justify-between border border-[#E5E5E5] rounded-[0.417rem] p-2">
                                <div className="flex items-center gap-1 max-w-full">
                                    <Text size="sm" className="font-semibold flex-shrink-0 whitespace-nowrap">
                                        {t('Data Used for Measurement :')}
                                    </Text>

                                    {experiment?.step > 4 || viewOnly ? (
                                        <Text
                                            size="sm"
                                            className="px-2 bg-primary rounded-[0.717rem] !leading-4 flex-shrink truncate "
                                            title={experiment?.data_mappings?.delta_measurement_table_name ?? experiment?.data_mappings?.user_created_measurement_table_name ?? '-'}
                                        >
                                            {experiment?.data_mappings?.delta_measurement_table_name ?? experiment?.data_mappings?.user_created_measurement_table_name ?? '-'}
                                        </Text>
                                    ) : (
                                        <>
                                            <Icon name="clock" className="text-[0.833rem] text-[#C6BA6A]" />
                                            <Text size="sm" className="text-[#C6BA6A]">
                                                {t('Pending')}
                                            </Text>
                                            <Button
                                                theme="secondary"
                                                size="xxs"
                                                onClick={() => {
                                                    measurementModalRef?.current?.showModal()
                                                }}
                                            >
                                                {t('Select Data')}
                                            </Button>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="grid grid-cols-2 gap-3 mt-[0.15rem]">
                        {!viewOnly && (
                            <>
                                <span></span>
                                <Text size="sm" className="text-[#616161]">
                                    {t('If your measurement data is already in tables, select it to enable automated measurement.')}
                                </Text>
                            </>
                        )}
                    </div>

                    <div>
                        <div className="flex items-center justify-between mt-2">
                            <Text size="md" className="font-semibold">
                                {t('Group Details:')}
                            </Text>

                            <button type="button" aria-label={t('toggle metrics expand')} data-testid="metrics-toggle-button" onClick={() => setShowMetriceExpand(!showMetriceExpand)} className="icon-button">
                                <Icon name="down" className={`transition-all ${showMetriceExpand ? 'rotate-0' : '-rotate-90'}`} />
                            </button>
                            {/* <Icon name="down" onClick={() => setShowGroupExpand(!showGroupExpand)} className={`transition-all ${showGroupExpand ? 'rotate-0' : '-rotate-90'}`}></Icon> */}
                        </div>
                        <div className={`mt-1 overflow-hidden transition-[max-height] ${showGroupExpand ? 'max-h-auto' : 'max-h-0'}`}>
                            <div className="grid grid-cols-2 gap-3">
                                {experiment?.experiment_metadatas?.interventions[0]?.group_configs?.map((group: any) => {
                                    const key = `group-${group.name || JSON.stringify(group)}`
                                    return <Groups group={group} grp_sum={grp_sum} groupTotal={experiment?.experiment_metadatas?.total_group_size} key={key} />
                                })}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center justify-between mt-3 ">
                        <div className="flex items-center gap-3"></div>
                        {viewOnly ? (
                            <div className="flex items-center gap-3">
                                <Button
                                    theme="secondary"
                                    size="xs"
                                    styleType="outline"
                                    onClick={() => {
                                        success()
                                        _ref.current.close()
                                    }}
                                >
                                    {t('Close')}
                                </Button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-3">
                                <Button
                                    theme="secondary"
                                    size="xs"
                                    styleType="outline"
                                    onClick={() => {
                                        trackEvent('wizard_review_modal_closed', { completed: false })
                                        _ref.current.close()
                                    }}
                                    data-testid="cancel-button"
                                >
                                    {t('Cancel')}
                                </Button>
                                <Button
                                    theme="secondary"
                                    size="xs"
                                    className="text-white"
                                    onClick={() => {
                                        trackEvent('wizard_submit_clicked', {})
                                        updateExperiment()
                                    }}
                                    data-testid="submit-button"
                                >
                                    {t('Submit')}
                                </Button>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            {!viewOnly && (
                <form method="dialog" className="modal-backdrop">
                    <button type="button">{t('Close')}</button>
                </form>
            )}
        </dialog>
    )
}

export default ExperimentReviewModal
